import { useTranslation } from 'react-i18next';
import { usePercentageRangeLabel } from '../hooks/usePercentageRangeLabel';
import type { PoolFilterKey, PoolFilterState } from '../hooks/usePoolFilters';
import { useProfessionalsChip } from '../hooks/useProfessionalsChip';
import type { PoolFilterOptions } from '../types/Checks';
import { type FilterChip, FilterChipList } from './FilterChipList';

type PoolFilterChipsProps = {
  filterState: PoolFilterState;
  /** Nomes de Projeto/Tag; enquanto carrega, o chip mostra o id. */
  filterOptions: PoolFilterOptions | undefined;
};

export function PoolFilterChips({ filterState, filterOptions }: PoolFilterChipsProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const { filters, professionals } = filterState;
  const percentageRangeLabel = usePercentageRangeLabel();
  const professionalsChip = useProfessionalsChip();

  const chips: FilterChip<PoolFilterKey>[] = [];
  const contains = (key: PoolFilterKey, field: string, value: string | undefined) => {
    if (value) chips.push({ key, text: t('alocacao:filtros.chips.contem', { field, value }) });
  };
  const selected = (key: PoolFilterKey, field: string, name: string) =>
    chips.push({ key, text: t('alocacao:filtros.chips.selecionado', { field, name }) });
  const names = (ids: number[], options: { id: number; label: string }[]) =>
    ids.map((id) => options.find((option) => option.id === id)?.label ?? String(id)).join(', ');

  const professionalsText = professionalsChip(professionals);
  if (professionalsText) chips.push({ key: 'professional', ...professionalsText });
  contains(
    'professionalSearch',
    t('alocacao:consultas.colunas.profissional'),
    filters.professionalSearch,
  );
  contains('profileSearch', t('alocacao:consultas.colunas.perfil'), filters.profileSearch);
  if (filters.projectIds?.length) {
    const projects = (filterOptions?.projects ?? []).map((p) => ({
      id: p.id,
      label: `${p.axisCode} - ${p.name}`,
    }));
    selected('projectIds', t('alocacao:tabela.projeto'), names(filters.projectIds, projects));
  }

  const percentage = percentageRangeLabel(filters.minPercentage, filters.maxPercentage);
  if (percentage) chips.push({ key: 'percentage', text: percentage });

  if (filters.billable !== undefined) {
    selected(
      'billable',
      t('alocacao:tabela.faturando'),
      filters.billable ? t('common:yes') : t('common:no'),
    );
  }
  if (filters.notified !== undefined) {
    selected(
      'notified',
      t('alocacao:consultas.colunas.avisado'),
      filters.notified ? t('common:yes') : t('common:no'),
    );
  }
  contains('notes', t('alocacao:filtros.anotacoes'), filters.notes);
  if (filters.tagIds?.length) {
    const tags = (filterOptions?.tags ?? []).map((tag) => ({ id: tag.id, label: tag.name }));
    selected('tagIds', t('alocacao:form.tags'), names(filters.tagIds, tags));
  }

  return (
    <FilterChipList
      chips={chips}
      onRemove={filterState.clearFilter}
      onClear={filterState.clearFilters}
    />
  );
}
